import { TaskStatus as TaskStatusEnum } from '@/services/enums';
import { useRef, useCallback, useEffect } from 'react';

export interface TaskStatus {
  status: TaskStatusEnum;
  [key: string]: any;
}

export interface UseTaskPollingOptions<T extends TaskStatus = TaskStatus> {
  /**
   * 轮询函数，返回 Promise<{ success: boolean; data?: T; message?: string }>
   */
  pollFn: () => Promise<{ success: boolean; data?: T; message?: string }>;

  /**
   * 轮询间隔（毫秒），默认 3000
   */
  interval?: number;

  /**
   * 是否立即执行第一次轮询，默认 true
   */
  immediate?: boolean;

  /**
   * 当任务状态更新时的回调
   */
  onStatusUpdate?: (status: T) => void;

  /**
   * 当任务完成时的回调
   */
  onCompleted?: (status: T) => void;

  /**
   * 当任务失败时的回调
   */
  onFailed?: (status: T) => void;

  /**
   * 当接口返回 success: false 时的回调
   */
  onError?: (message?: string) => void;

  /**
   * 当轮询出错时的回调（网络错误等）
   */
  onPollError?: (error: any) => void;

  /**
   * 判断任务是否已完成（停止轮询的条件）
   * 默认：status === 'completed' || status === 'failed'
   */
  isFinished?: (status: T) => boolean;

  /**
   * 清理函数，在停止轮询时调用（如清理 localStorage）
   */
  onCleanup?: () => void;

  /**
   * 是否启用轮询，默认 true
   */
  enabled?: boolean;
}

/**
 * 通用的任务状态轮询 Hook
 *
 * // 开始轮询
 * startPolling();
 *
 * // 停止轮询
 * stopPolling();
 * ```
 */
export function useTaskPolling<T extends TaskStatus = TaskStatus>(options: UseTaskPollingOptions<T>) {
  const {
    pollFn,
    interval = 3000,
    immediate = true,
    onStatusUpdate,
    onCompleted,
    onFailed,
    onError,
    onPollError,
    isFinished = (status) => status.status === 'completed' || status.status === 'failed',
    onCleanup,
    enabled = true,
  } = options;

  const pollingRef = useRef<number | null>(null);
  const isPollingStoppedRef = useRef(false);
  const isPollingRef = useRef(false);
  const pollFnRef = useRef(pollFn);
  const intervalRef = useRef(interval);
  const immediateRef = useRef(immediate);
  const onStatusUpdateRef = useRef(onStatusUpdate);
  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);
  const onErrorRef = useRef(onError);
  const onPollErrorRef = useRef(onPollError);
  const isFinishedRef = useRef(isFinished);
  const onCleanupRef = useRef(onCleanup);

  // 同步所有回调函数和配置到 ref，确保总是使用最新的值
  useEffect(() => {
    pollFnRef.current = pollFn;
    intervalRef.current = interval;
    immediateRef.current = immediate;
    onStatusUpdateRef.current = onStatusUpdate;
    onCompletedRef.current = onCompleted;
    onFailedRef.current = onFailed;
    onErrorRef.current = onError;
    onPollErrorRef.current = onPollError;
    isFinishedRef.current = isFinished;
    onCleanupRef.current = onCleanup;
  }, [pollFn, interval, immediate, onStatusUpdate, onCompleted, onFailed, onError, onPollError, isFinished, onCleanup]);

  // stopPolling 不再依赖 onCleanup，使用 ref 访问，确保函数引用稳定
  const stopPolling = useCallback(() => {
    isPollingStoppedRef.current = true;
    isPollingRef.current = false;
    if (pollingRef.current !== null) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    // 通过 ref 访问 onCleanup，避免依赖变化
    if (onCleanupRef.current) {
      onCleanupRef.current();
    }
  }, []); // 空依赖数组，确保函数引用稳定

  // 将 poll 函数提取到外部，使用 ref 存储，确保递归调用时使用最新的函数
  const pollRef = useRef<(() => Promise<void>) | null>(null);

  const startPolling = useCallback(() => {
    // 如果已经正在轮询，不重复启动
    if (isPollingRef.current && !isPollingStoppedRef.current) {
      return;
    }

    // 重置停止标志
    isPollingStoppedRef.current = false;
    isPollingRef.current = true;

    // 定义 poll 函数 - 先声明，然后赋值给 ref，确保递归调用时使用正确的引用
    let poll: () => Promise<void>;

    poll = async () => {
      // 如果已经停止轮询，直接返回
      if (isPollingStoppedRef.current) {
        return;
      }

      try {
        const response = await pollFnRef.current();

        // 再次检查，防止并发请求
        if (isPollingStoppedRef.current) {
          return;
        }

        if (response.success) {
          // 如果 success 为 true，但 data 可能为 null/undefined（任务可能还在初始化）
          if (response.data) {
            const status = response.data;

            // 调用状态更新回调
            if (onStatusUpdateRef.current) {
              onStatusUpdateRef.current(status);
            }

            // 检查是否已完成
            if (isFinishedRef.current(status)) {
              // 停止轮询
              stopPolling();

              // 调用相应的回调
              if (status.status === 'completed' && onCompletedRef.current) {
                onCompletedRef.current(status);
              } else if (status.status === 'failed' && onFailedRef.current) {
                onFailedRef.current(status);
              }
            } else {
              // 任务仍在处理中，延迟后继续轮询
              // 清除之前的定时器（如果存在）
              if (pollingRef.current !== null) {
                clearTimeout(pollingRef.current);
              }
              // 保存 poll 函数的引用，确保定时器回调中能正确调用
              const currentPoll = poll;
              pollingRef.current = window.setTimeout(() => {
                // 直接调用保存的 poll 函数引用
                currentPoll();
              }, intervalRef.current);
            }
          } else {
            // success 为 true 但 data 为 null/undefined，可能是任务还在初始化，继续轮询
            // 清除之前的定时器（如果存在）
            if (pollingRef.current !== null) {
              clearTimeout(pollingRef.current);
            }
            // 保存 poll 函数的引用，确保定时器回调中能正确调用
            const currentPoll = poll;
            pollingRef.current = window.setTimeout(() => {
              // 直接调用保存的 poll 函数引用
              currentPoll();
            }, intervalRef.current);
          }
        } else {
          // 如果接口返回 success: false，停止轮询
          stopPolling();
          if (onErrorRef.current) {
            onErrorRef.current(response.message);
          }
        }
      } catch (error: any) {
        // 即使出错，也继续轮询（延迟后重试），但需要检查是否已停止
        if (!isPollingStoppedRef.current) {
          // 清除之前的定时器（如果存在）
          if (pollingRef.current !== null) {
            clearTimeout(pollingRef.current);
          }
          // 保存 poll 函数的引用，确保定时器回调中能正确调用
          const currentPoll = poll;
          pollingRef.current = window.setTimeout(() => {
            // 直接调用保存的 poll 函数引用
            currentPoll();
          }, intervalRef.current);
        }
        if (onPollErrorRef.current) {
          onPollErrorRef.current(error);
        }
      }
    };

    // 将 poll 函数存储到 ref 中，确保递归调用时使用正确的引用
    pollRef.current = poll;

    // 立即执行一次（如果 immediate 为 true）
    if (immediateRef.current) {
      poll();
    } else {
      pollingRef.current = window.setTimeout(() => {
        if (pollRef.current) {
          pollRef.current();
        }
      }, intervalRef.current);
    }
  }, [stopPolling]);

  // 当 enabled 变化时，自动启动或停止轮询
  useEffect(() => {
    if (enabled) {
      // 如果 enabled 为 true，且当前没有在轮询，则启动轮询
      // 注意：即使 isPollingStoppedRef.current 为 true，也要重置并启动（因为 enabled 变为 true 表示需要轮询）
      if (!isPollingRef.current) {
        startPolling();
      }
    } else {
      // 如果 enabled 为 false，停止轮询
      stopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // 组件卸载时清理 - 不依赖 stopPolling，直接使用稳定的清理逻辑
  useEffect(() => {
    return () => {
      isPollingStoppedRef.current = true;
      isPollingRef.current = false;
      if (pollingRef.current !== null) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      // 通过 ref 访问 onCleanup
      if (onCleanupRef.current) {
        onCleanupRef.current();
      }
    };
  }, []); // 空依赖数组，只在组件卸载时执行一次

  return {
    startPolling,
    stopPolling,
    isPolling: isPollingRef.current,
  };
}
